import { useEffect, useRef, useState } from "react";
import { getCustomisationValue, getGroupData } from "../../EditablePreview";
import VisualisedNode, { IMAGE_MODES } from "../VisualisedNode";
import './advanced-timer.css';
import { HEX_TO_RGBA } from "../../../../../../../helpers/Colours";
import InlineProgressBar from "../progress-bar/inline-progress-bar";

const AdvancedTimer = (props) => {
    // console.log("AdvancedTimer props:", props);
    const customisationVars = props.node.customisationVars;

    const timerCustomisationTab = props.node?.config?.groupSettings?.dataLink?.split('.')[0] ?? null;
    if (timerCustomisationTab === null) {
        return null;
    }
    const settings = customisationVars?.[timerCustomisationTab] ?? null;
    if (settings === null) {
        return null;
    }

    // console.log("AdvancedTimer settings:", settings);
    const progressPerc = (props.fillPerc ?? 0.7);

    const isMovesCounter = settings?.countText ? true : false;

    /**
     * 
     * @param {Object} obj The original object
     * @param {Object} overrideObj The object with new values to apply
     * @param {Boolean} addNewProps Only replace matching properties, or add new ones?
     * @param {Array} alternativePropNames[] An array of alternative property names to check for (if we are looking for border, but the override obj uses panelBorder: [{original: 'border', override: 'panelBorder'}])
     * @returns 
     */
    const overrideObjectProps = (obj, overrideObj, addNewProps = false, alternativePropNames = [], onlyAddNew = false) => {
        const getOverrideProp = (prop) => {
            for (let i = 0; i < alternativePropNames.length; i++) {
                if (prop === alternativePropNames[i].override) {
                    return alternativePropNames[i].original;
                }
            }
            return false;
        };
        for (let prop in overrideObj) {
            const overrideProp = getOverrideProp(prop) || prop;
            if (addNewProps || obj.hasOwnProperty(overrideProp)) {
                // console.log('Overriding prop: ', overrideProp, obj[overrideProp], ' with value: ', prop, overrideObj[prop]);
                if (!onlyAddNew) {
                    obj[overrideProp] = overrideObj[prop];
                    if (addNewProps) {
                        obj[prop] = overrideObj[prop];
                    }
                } else {
                    if (!obj.hasOwnProperty(overrideProp)) {
                        obj[overrideProp] = overrideObj[prop];
                    }
                    if (addNewProps && !obj.hasOwnProperty(prop)) {
                        obj[prop] = overrideObj[prop];
                    }
                }
            }
        }
        // console.log('Overridden object: ', obj, ' with overrideObj: ', overrideObj, ' addNewProps: ', addNewProps);
        return obj;
    }

    const addBgStyle = (styleObj, originalCustomisationData) => {
        // Using our overrideObjectProps function allows us to accept alternative property names for the style object
        const customisationData = {};
        const styleObjAlternativePropNames = [
            {original: 'bgType', override: 'type'},
            {original: 'bgColour', override: 'colour'},
            {original: 'bgOpacity', override: 'opacity'},
            {original: 'bgGradientAngle', override: 'gradientAngle'},
            {original: 'bgGradientFrom', override: 'gradientFrom'},
            {original: 'bgGradientTo', override: 'gradientTo'},
            {original: 'bgImage', override: 'image'},
        ]
        overrideObjectProps(customisationData, originalCustomisationData, true, styleObjAlternativePropNames);

        if (customisationData.bgType === 'solidColour') {
            styleObj.backgroundColor = HEX_TO_RGBA(customisationData.bgColour, customisationData.bgOpacity ? customisationData.bgOpacity / 100 : 1);
        } else if (customisationData.bgType === 'gradient') {
            styleObj.background = `linear-gradient(${customisationData.bgGradientAngle}deg, ${HEX_TO_RGBA(customisationData.bgGradientFrom, customisationData.bgOpacity ? customisationData.bgOpacity / 100 : 1)}, ${HEX_TO_RGBA(customisationData.bgGradientTo, customisationData.bgOpacity ? customisationData.bgOpacity / 100 : 1)})`;
        } else if (customisationData.bgType === 'image') {
            styleObj.backgroundImage = `url(${customisationData.bgImage.srcUrl})`;
            if (!styleObj.backgroundSize) {
                styleObj.backgroundSize = 'auto';
            }
            if (!styleObj.backgroundPosition) {
                styleObj.backgroundPosition = 'center';
            }
            if (!styleObj.backgroundRepeat) {
                styleObj.backgroundRepeat = 'repeat';
            }
        } else if (customisationData.bgType === 'none') {
            styleObj.backgroundColor = 'transparent';
        }
        return styleObj;
    }

    // Our progress bar expects is customisation data in a slightly different structure
    const getBarCustomisationObject = () => {
        const customisationData = settings;
        const barCustomisationObject = {};

        barCustomisationObject.shapeAndPosition = {shape: customisationData.appearance.shape};
        barCustomisationObject.appearance = customisationData.appearance;
        barCustomisationObject.appearance.background[0].fillType = customisationData.appearance.background[0].fillType || customisationData.appearance.background[0].bgType || 'solid';

        return barCustomisationObject;
    }

    // This doesn't actually create a radial pie, it create a square pie
    // We are using a div with overflow hidden and corder roundness to allow square radial and rounded / circular radial
    const getRadialSvg = () => {
        const size = settings.radialAppearance.shape[0].size * 2 + 50;

        const cornerPointsPerc = [
            {x: size / 2, y: 0, perc: 0},
            {x: size, y: 0, perc: 0.125},
            {x: size, y: size, perc: 0.375},
            {x: 0, y: size, perc: 0.625},
            {x: 0, y: 0, perc: 0.875},
            {x: size / 2, y: 0, perc: 1},
        ]

        const perc = 1 - (props.fillPerc ?? 0.7);

        const pathPointsData = cornerPointsPerc.map((point, index) => {
            if (perc > point.perc && point.perc < 1) {
                const nextPoint = cornerPointsPerc[index + 1];
                if (perc < nextPoint.perc) {
                    const percDiff = nextPoint.perc - point.perc;
                    const percInSegment = (perc - point.perc) / percDiff;
                    const x = point.x + (nextPoint.x - point.x) * percInSegment;
                    const y = point.y + (nextPoint.y - point.y) * percInSegment;
                    return `${x},${y}`;
                } else {
                    return null;
                }
            } else {
                return `${point.x},${point.y}`;
            }
        });
        let pathPointsString = `${size / 2},${size / 2} `;
        for (let i = 0; i < pathPointsData.length; i++) {
            if (pathPointsData[i]) {
                pathPointsString += pathPointsData[i] + ' ';
            }
        }
        pathPointsString += `${size / 2},${size / 2}`;

        return (
            <svg className='radial-svg' viewBox={`0 0 ${size} ${size}`}>
                <clipPath id={'radial-mask'}>
                    <polygon points={pathPointsString} fill='#FF0000' />
                </clipPath>
            </svg>
        );
    }

    const timerPos = settings.settings.visualSettings[0].timerGraphic === 'radial' ? settings.radialAppearance.position[0].position : settings.appearance.position[0].position;
    const timerBarPosString = settings.appearance.position[0].position;
    const timerXOffset = settings.settings.visualSettings[0].timerGraphic === 'radial' ? settings.radialAppearance.position[0].xOffset : settings.appearance.position[0].xOffset;
    const timerYOffset = settings.settings.visualSettings[0].timerGraphic === 'radial' ? settings.radialAppearance.position[0].yOffset : settings.appearance.position[0].yOffset;
    const timerPosCssVars = {
        '--offsetX': /*(timerXOffset * props.previewScale)*/ 0 + 'px',
        '--offsetY': /*(timerYOffset * props.previewScale)*/ 0 + 'px',
    }

    const cssVars = {
        "--barStyleWidth": `${(settings.appearance.shape[0].length / 100 * 1920) * props.previewScale}px`,
        "--barStyleHeight": `${settings.appearance.shape[0].thickness * props.previewScale}px`,
        "--radialSize": `${(settings.radialAppearance.shape[0].size * 2 + 50) * props.previewScale}px`,
        "--containerWidth": `${(settings.radialAppearance.shape[0].size * 2 + 150) * props.previewScale}px`,
        "--radialRoundness": `${settings.radialAppearance.shape[0].roundness / 2}%`,
        "--radialOutlineWidth": `${settings.radialAppearance.outline[0].addBorder ? settings.radialAppearance.outline[0].thickness * props.previewScale : 0}px`,
        "--radialOutlineColour": settings.radialAppearance.outline[0].colour,
        "--offsetX": ((settings?.countdown?.template[0].xOffset ?? settings?.countText?.template[0].xOffset ?? 0) * props.previewScale) + 'px',
        "--offsetY": ((settings?.countdown?.template[0].yOffset ?? settings?.countText?.template[0].yOffset ?? 0) * props.previewScale) + 'px',
        "--headingFont": settings?.countdown?.heading[0].copyFont ?? settings?.countText?.heading[0].copyFont,
        "--headingFontSize": ((settings?.countdown?.heading[0].copyFontSize ?? settings?.countText?.heading[0].copyFontSize ?? 12) * props.previewScale) + 'px',
        "--headingColour": settings?.countdown?.heading[0].copyColour ?? settings?.countText?.heading[0].copyColour,
        "--headingFontWeight": (settings?.countdown?.heading[0].isBold ?? settings?.countText?.heading[0].isBold) ? 'bold' : 'normal',
        "--timerFont": settings?.countdown?.timer[0].timerFont,
        "--timerFontSize": ((settings?.countdown?.timer[0].timerFontSize ?? 12) * props.previewScale) + 'px',
        "--timerColour": settings?.countdown?.timer[0].timerColour,
        "--timerFontWeight": settings?.countdown?.timer[0].timerIsBold ? 'bold' : 'normal',
        "--hmsHeadingFont": settings?.countdown?.timer[0].hmsFont,
        "--hmsHeadingFontSize": ((settings?.countdown?.timer[0].hmsFontSize ?? 12) * props.previewScale) + 'px',
        "--hmsHeadingColour": settings?.countdown?.timer[0].hmsColour,
        "--hmsHeadingFontWeight": settings?.countdown?.timer[0].hmdIsBold ? 'bold' : 'normal',
        "--countTextFont": settings?.countText?.countText[0].font,
        "--countTextFontSize": ((settings?.countText?.countText[0].fontSize ?? 12) * props.previewScale) + 'px',
        "--countTextColour": settings?.countText?.countText[0].colour,
        "--countTextFontWeight": settings?.countText?.countText[0].isBold ? 'bold' : 'normal',
    }
    const barWidth = (settings.appearance.shape[0].length / 100 * 1920) * props.previewScale;
    const barHeight = settings.appearance.shape[0].thickness * props.previewScale;

    const syncMessageHeight = settings.settings.visualSettings[0].timerGraphic === 'bar' ? (settings.appearance.shape[0].thickness * props.previewScale) + 'px' : ((settings.radialAppearance.shape[0].size * 2 + 50) / 2 * props.previewScale) + 'px';

    // Our fill object properties don't exactly match what we need for addingBgStyle,
    // so we need to create a new object with the correct properties
    const fillBgObj = {};
    overrideObjectProps(fillBgObj, settings.radialAppearance.foreground[0], true, 
        [
            {original: 'bgType', override: 'fgType'},
            {original: 'bgColour', override: 'fgColour'},
            {original: 'bgGradientAngle', override: 'fgGradientAngle'},
            {original: 'bgGradientFrom', override: 'fgGradientFrom'},
            {original: 'bgGradientTo', override: 'fgGradientTo'},
            {original: 'bgOpacity', override: 'fgOpacity'},
            {original: 'bgImage', override: 'fgImage'}
        ]
    );

    const animClasses = {
        showHeading: 'adt-anim-in-1',
        showGraphic: settings?.countdown?.template[0].showHeading ? 'adt-anim-in-2' : 'adt-anim-in-1',
        showCountdown: settings?.countdown?.template[0].showHeading && settings?.settings.visualSettings[0].timerGraphic !== 'none' ? 'adt-anim-in-3' : settings?.countdown?.template[0].showHeading || settings?.settings.visualSettings[0].timerGraphic !== 'none' ? 'adt-anim-in-2' : 'adt-anim-in-1',
    }

    const h1Style = {
        margin: `0 0 ${20 * props.previewScale}px 0`,
        width: '100%',
    }
    const pStyle = {
        margin: `0 0 ${10 * props.previewScale}px 0`,
        width: '100%',
    }
    const margin20Pix = 20 * props.previewScale;

    // console.log("AdvancedTimer render with timerBarPosString:", timerBarPosString);
    const timerBarMarginStyle = {
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        marginBottom: 0,
    }
    if (timerBarPosString.includes('top')) {
        timerBarMarginStyle.marginTop = '-20px';
    }
    if (timerBarPosString.includes('bottom')) {
        timerBarMarginStyle.marginBottom = '-20px';
    }
    if (timerBarPosString.includes('left')) {
        timerBarMarginStyle.marginLeft = '-20px';
    }
    if (timerBarPosString.includes('right')) {
        timerBarMarginStyle.marginRight = '-20px';
    }

    return (
        <div className='advanced-timer' style={cssVars}>
            <span style={timerPosCssVars}>
                {/* <div className={`advanced-timer ${timerPos}`}> */}
                    {!settings.settings.visualSettings[0].showCountdown && settings?.countdown?.template[0].showHeading && settings?.countdown?.heading[0].copy !== '' &&
                        <h1 className="advanced-timer-h1" style={h1Style}>{settings?.countdown?.heading[0].copy}</h1>
                    }
                    {settings.settings.visualSettings[0].timerGraphic === 'bar' &&
                        <VisualisedNode {...props} node={props.node.subNodes[1]} margin={20 / props.previewScale} style={timerBarMarginStyle} />
                    }
                    {/* <VisualisedNode {...props} node={props.node.subNodes[1]} style={{minWidth: barWidth + 'px'}}>
                        <div className={`advanced-timer-timer-bar-holder ${animClasses.showGraphic}`} style={{marginBottom: margin20Pix + 'px'}}>
                            <InlineProgressBar fillPerc={progressPerc >= 0 ? progressPerc : 0} width={barWidth} height={barHeight} customSettings={getBarCustomisationObject()} useOutline={settings.appearance.outline[0].addBorder} />
                            {props.timeUpMessage && progressPerc <= 0 && 
                                <div className='advanced-time-over-message-holder-outer' style={{height: syncMessageHeight}}>
                                    <div className={`advanced-time-over-message-holder`}>
                                        {props.timeUpMessage}
                                    </div>
                                </div>
                            }
                        </div>
                    </VisualisedNode> */}
                    {settings.settings.visualSettings[0].timerGraphic === 'radial' &&
                        <VisualisedNode {...props} node={props.node.subNodes[0]} />
                    }
                    {/*<VisualisedNode {...props} node={props.node.subNodes[0]}>
                        <div className={`advanced-timer-timer-radial-holder ${animClasses.showGraphic}`} style={{...addBgStyle({backgroundSize:'cover', backgroundRepeat:'no-repeat'}, settings.radialAppearance.background[0]), marginBottom: margin20Pix + 'px'}}>
                            {progressPerc < 1 &&
                                <>
                                    <div className='inner'>
                                        {getRadialSvg()}
                                    </div>
                                    <div className='inner' style={addBgStyle({clipPath: `url(#radial-mask)`, backgroundSize:'cover', backgroundRepeat:'no-repeat'}, fillBgObj)}></div>
                                </>
                            }
                            {props.timeUpMessage && progressPerc <= 0 && 
                                <div className='advanced-time-over-message-holder-outer' style={{height: syncMessageHeight}}>
                                    <div className={`advanced-time-over-message-holder`}>
                                        {props.timeUpMessage}
                                    </div>
                                </div>
                            }
                        </div>
                    </VisualisedNode>
                    */}
                {/* </div> */}
            </span>
            {(settings.settings.visualSettings[0].showCountdown || settings.settings.visualSettings[0].showCountText) &&
                <VisualisedNode {...props} node={props.node.subNodes[2]}>
                    {isMovesCounter &&
                        <div className={`countdown-row`} style={{...cssVars, margin: `0`}}>
                            <div className='countdown-col-grow'>

                                <div className='view-row'>
                                    {/* <div className='view-column grow'></div> */}
                                    {settings.countText.template[0].showHeading && settings.countText.heading[0].copy !== '' &&
                                        <VisualisedNode {...props} node={props.node.subNodes[2].subNodes[0]} style={h1Style}>
                                            {/* <h1 className="advanced-timer-h1" style={h1Style}>{settings.countdown.heading[0].copy}</h1> */}
                                        </VisualisedNode>
                                    }
                                    {/* <div className='view-column grow'></div> */}
                                </div>

                                <div className='view-row'>
                                    <div className='countdown-col-grow'>
                                        <VisualisedNode {...props} node={props.node.subNodes[2].subNodes[1]} style={pStyle}>
                                            <p className="advanced-moves-p" style={pStyle}>
                                                <>{`${settings.countText.countText[0].beforeCopy} ${!settings.countText.countText[0].countDown ? `3 ${settings.countText.countText[0].dividerCopy} ` : ''} 9 ${settings.countText.countText[0].afterCopy}`}</>
                                            </p>
                                        </VisualisedNode>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    {settings?.countdown?.template[0].countdownStyle === 'style1' &&
                        <div className={`countdown-row`} style={{...cssVars, margin: `0`}}>
                            <div className='countdown-col-grow'>

                                <div className='view-row'>
                                    {/* <div className='view-column grow'></div> */}
                                    {settings.countdown.template[0].showHeading && settings.countdown.heading[0].copy !== '' &&
                                        <VisualisedNode {...props} node={props.node.subNodes[2].subNodes[0]} style={h1Style}>
                                            {/* <h1 className="advanced-timer-h1" style={h1Style}>{settings.countdown.heading[0].copy}</h1> */}
                                        </VisualisedNode>
                                    }
                                    {/* <div className='view-column grow'></div> */}
                                </div>
                                <div className='view-row'>
                                    {/* <div className='view-column grow'></div> */}
                                    {props.showHours &&
                                        <>
                                            <div className='countdown-col'>
                                                <p className="advanced-timer-p" style={pStyle}>01{/*Math.floor(countDownTracker.timeLeft / 3600) < 10 && '0'*/}{/*Math.floor(countDownTracker.timeLeft / 3600)*/}</p>
                                                <h2 className="advanced-timer-h2">{settings.countdown.timer[0].hoursCopy}</h2>
                                            </div>
                                            <div className='countdown-col'>
                                                <p className="advanced-timer-p" style={pStyle}>01{/*Math.floor(countDownTracker.timeLeft / 60) % 60 < 10 && '0'*/}{/*Math.floor(countDownTracker.timeLeft / 60) % 60*/}</p>
                                                <h2 className="advanced-timer-h2">{settings.countdown.timer[0].minsCopy}</h2>
                                            </div>
                                            {props.showSecs &&
                                                <div className='countdown-col'>
                                                    <p className="advanced-timer-p" style={pStyle}>01{/*Math.floor(countDownTracker.timeLeft % 60) < 10 && '0'*/}{/*Math.floor(countDownTracker.timeLeft % 60)*/}</p>
                                                    <h2 className="advanced-timer-h2">{settings.countdown.timer[0].secsCopy}</h2>
                                                </div>
                                            }
                                        </>
                                    }
                                    {!props.showHours && props.showMins &&
                                        <>
                                            <div className='countdown-col'>
                                                <p className="advanced-timer-p" style={pStyle}>99{/*Math.floor(countDownTracker.timeLeft / 60)*/}</p>
                                                <h2 className="advanced-timer-h2">{settings.countdown.timer[0].minsCopy}</h2>
                                            </div>
                                            {props.showSecs &&
                                                <div className='countdown-col'>
                                                    <p className="advanced-timer-p" style={pStyle}>01{/*Math.floor(countDownTracker.timeLeft % 60) < 10 && '0'*/}{/*Math.floor(countDownTracker.timeLeft % 60)*/}</p>
                                                    <h2 className="advanced-timer-h2">{settings.countdown.timer[0].secsCopy}</h2>
                                                </div>
                                            }
                                        </>
                                    }
                                    {!props.showHours && !props.showMins && props.showSecs &&
                                        
                                            <div className='countdown-col-grow'>
                                                <VisualisedNode {...props} node={props.node.subNodes[2].subNodes[1]}>
                                                    <p className="advanced-timer-p" style={pStyle}>99{/*Math.ceil(countDownTracker.timeLeft)*/}</p>
                                                </VisualisedNode>
                                                <VisualisedNode {...props} node={props.node.subNodes[2].subNodes[1].subNodes[0]}>
                                                    {/* <h2 className="advanced-timer-h2">{settings.countdown.timer[0].secsCopy}</h2> */}
                                                </VisualisedNode>
                                            </div>
                                        
                                    }
                                    {/* <div className='view-column grow'></div> */}
                                </div>
                            </div>
                        </div>
                    }
                    {settings?.countdown?.template[0].countdownStyle === 'style2' &&
                        <div className={`countdown-row`} style={{...cssVars, margin: `0`}}>
                            <div className='countdown-col-grow'>

                                <div className='view-row'>
                                    {/* <div className='view-column grow'></div> */}
                                    {settings.countdown.template[0].showHeading && settings.countdown.heading[0].copy !== '' &&
                                        <VisualisedNode {...props} node={props.node.subNodes[2].subNodes[0]} style={h1Style}>
                                            {/* <h1 className="advanced-timer-h1" style={h1Style}>{settings.countdown.heading[0].copy}</h1> */}
                                        </VisualisedNode>
                                    }
                                    {/* <div className='view-column grow'></div> */}
                                </div>
                                <div className='view-row'>

                                    {/* <div className='countdown-col-grow'></div> */}
                                    {props.showHours &&
                                        <>
                                            <div className='countdown-col-shrink'>
                                                <p className="advanced-timer-p" style={pStyle}>
                                                    01
                                                    {/*Math.floor(countDownTracker.timeLeft / 3600) < 10 && '0'*/}
                                                    {/*Math.floor(countDownTracker.timeLeft / 3600)*/}
                                                </p>
                                            </div>
                                            <div className='countdown-col-shrink'>
                                                <p className="advanced-timer-p" style={pStyle}>:</p>
                                            </div>
                                            <div className='countdown-col-shrink'>
                                                <p className="advanced-timer-p" style={pStyle}>
                                                    01
                                                    {/*Math.floor(countDownTracker.timeLeft / 60) % 60 < 10 && '0'*/}
                                                    {/*Math.floor(countDownTracker.timeLeft / 60) % 60*/}
                                                </p>
                                            </div>
                                            {props.showSecs &&
                                                <>
                                                    <div className='countdown-col-shrink'>
                                                        <p className="advanced-timer-p" style={pStyle}>:</p>
                                                    </div>
                                                    <div className='countdown-col-shrink'>
                                                        <p className="advanced-timer-p" style={pStyle}>
                                                            01
                                                            {/*Math.floor(countDownTracker.timeLeft % 60) < 10 && '0'*/}
                                                            {/*Math.floor(countDownTracker.timeLeft % 60)*/}
                                                        </p>
                                                    </div>
                                                </>
                                            }
                                        </>
                                    }
                                    {!props.showHours && props.showMins &&
                                        <>
                                            <div className='countdown-col-shrink'>
                                                <p className="advanced-timer-p" style={pStyle}>
                                                    99
                                                    {/*Math.floor(countDownTracker.timeLeft / 60)*/}
                                                </p>
                                            </div>
                                            {props.showSecs &&
                                                <>
                                                    <div className='countdown-col-shrink'>
                                                        <p className="advanced-timer-p" style={pStyle}>:</p>
                                                    </div>
                                                    <div className='countdown-col-shrink'>
                                                        <p className="advanced-timer-p" style={pStyle}>
                                                            01
                                                            {/*Math.floor(countDownTracker.timeLeft % 60) < 10 && '0'*/}
                                                            {/*Math.floor(countDownTracker.timeLeft % 60)*/}
                                                        </p>
                                                    </div>
                                                </>
                                            }
                                        </>
                                    }
                                    {!props.showHours && !props.showMins && props.showSecs &&
                                        
                                            <div className='countdown-col-grow'>
                                                <VisualisedNode {...props} node={props.node.subNodes[2].subNodes[1]}>
                                                    <p className="advanced-timer-p" style={pStyle}>
                                                        99
                                                        {/*Math.ceil(countDownTracker.timeLeft)*/}
                                                    </p>
                                                </VisualisedNode>
                                            </div>
                                        
                                    }
                                    {/* <div className='countdown-col-grow'></div> */}
                                </div>
                            </div>
                        </div>
                    }
                </VisualisedNode>
            }
        </div>


    );
}

export default AdvancedTimer;
